/**
 * Validerings-hjälpare för Excel-uppladdning av tillstånds- och kontraktskods-rader.
 *
 * Returnerar två lager av problem:
 *  - errors:   hårda fel som blockerar upload (felaktigt format, slutdatum före startdatum osv)
 *  - warnings: visas i preview men användaren kan ändå gå vidare (retroaktiv aktivering, gap > 30d osv)
 *
 * Allt här är klient-sidans validering. Backend ska göra samma + cross-row + DB-state-checkar i preview-endpointen.
 */

import { PhoneNumberUtil, PhoneNumberType } from "google-libphonenumber";

const MIN_REASONABLE_YEAR = 2020;
const MAX_REASONABLE_YEAR = 2050;
const RETROACTIVE_WARN_DAYS = 30;
const TRANSITION_GAP_WARN_DAYS = 30;

/**
 * Parse ett datum-värde (Date, string eller Excel serial-nummer).
 * Returnerar Date i UTC-midnatt eller null.
 *
 * Excel sparar datum som antal dagar sedan 1899-12-30 (med 1900-leap-year-bug
 * kompenserat). XLSX-lib utan `cellDates: true` returnerar serial som number.
 * Värdet 46142 = 2026-05-01.
 *
 * Timezone-hantering: xlsx med `cellDates: true` kan returnera Date i LOKAL
 * midnatt (t.ex. 2026-05-01 00:00 CEST = 2026-04-30 22:00 UTC). Om man sen
 * displayar via `.toISOString().slice(0,10)` blir det fel dag. För att slippa
 * den fällan normaliserar vi alltid till UTC-midnatt vid samma datum-
 * komponenter i lokal tid. Resultatet: `.toISOString()` ger rätt YYYY-MM-DD
 * oavsett browser-timezone (gäller positiva UTC-offsets, dvs Sverige).
 */
const toUtcMidnight = (d) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

export const parseDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return toUtcMidnight(value);
  }

  // Excel serial date: positivt tal i rimligt intervall (1968–2118 ≈ 25000–80000).
  if (typeof value === "number" && value > 25000 && value < 80000) {
    return new Date(Date.UTC(1899, 11, 30) + value * 86400000);
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;
  // Numerisk sträng från Excel (t.ex. "46142") — också Excel serial.
  if (/^\d{4,6}$/.test(trimmed)) {
    const n = Number(trimmed);
    if (n > 25000 && n < 80000) {
      return new Date(Date.UTC(1899, 11, 30) + n * 86400000);
    }
  }
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return null;
  return toUtcMidnight(d);
};

const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

/**
 * Validera ett enskilt datum.
 *
 * @param {*} value - Värdet från Excel-cellen
 * @param {Object} opts
 * @param {string} opts.field - Fältnamn för felmeddelandet (t.ex. "Startdatum")
 * @param {boolean} [opts.required] - Om värdet måste finnas
 * @param {boolean} [opts.mustBeFuture] - Hard error om datumet inte ligger i framtiden (för QueuedActiveDate)
 * @returns {{ error?: string, warning?: string, date?: Date }}
 */
export const validateDate = (value, { field, required = false, mustBeFuture = false } = {}) => {
  if (!value) {
    if (required) return { error: `${field} saknas` };
    return {};
  }

  const date = parseDate(value);
  if (!date) {
    return { error: `${field} har ogiltigt format: "${value}"` };
  }

  const year = date.getFullYear();
  if (year < MIN_REASONABLE_YEAR || year > MAX_REASONABLE_YEAR) {
    return { error: `${field} ligger utanför rimligt intervall (${MIN_REASONABLE_YEAR}–${MAX_REASONABLE_YEAR}): ${date.toISOString().slice(0, 10)}` };
  }

  if (mustBeFuture) {
    const today = startOfDay(new Date());
    if (startOfDay(date) <= today) {
      return { error: `${field} måste ligga i framtiden (idag är ${today.toISOString().slice(0, 10)})` };
    }
  }

  return { date };
};

/**
 * Validera en hel rad (typiskt en tilldelnings-rad med start/end).
 *
 * Returnerar både platta arrayer (för summering i fel/varnings-boxar) OCH
 * per-fält-objekt så UI kan highlighta exakta cellen som behöver fixas.
 *
 * @param {Object} row
 * @param {*} row.startDate
 * @param {*} row.endDate - kan vara null/undefined (öppet slut)
 * @returns {{
 *   errors: string[],
 *   warnings: string[],
 *   fieldErrors: { startDate?: string, endDate?: string },
 *   fieldWarnings: { startDate?: string, endDate?: string },
 *   parsed: { startDate?: Date, endDate?: Date }
 * }}
 */
export const validateAssignmentRow = (row) => {
  const errors = [];
  const warnings = [];
  const fieldErrors = {};
  const fieldWarnings = {};
  const parsed = {};

  // Klient-validering är medvetet PERMISSIV: vi gör bara format-checkar
  // (giltigt datum-format, rimligt år-intervall, slutdatum efter startdatum).
  // "Saknat startdatum"-fall är backend-auktoritet — backend vet om platsen
  // har ett befintligt tillstånd där bara slutdatumet ska uppdateras, vilket
  // klienten inte kan avgöra med säkerhet.
  const startResult = validateDate(row.startDate, { field: "Startdatum", required: false });
  if (startResult.error) {
    errors.push(startResult.error);
    fieldErrors.startDate = startResult.error;
  }
  if (startResult.date) parsed.startDate = startResult.date;

  const endResult = validateDate(row.endDate, { field: "Slutdatum", required: false });
  if (endResult.error) {
    errors.push(endResult.error);
    fieldErrors.endDate = endResult.error;
  }
  if (endResult.date) parsed.endDate = endResult.date;

  // Cross-check inom raden
  if (parsed.startDate && parsed.endDate && parsed.endDate < parsed.startDate) {
    const msg = "Slutdatum är före startdatum";
    errors.push(msg);
    fieldErrors.endDate = msg;
  }

  // Varningar (icke-blockerande)
  if (parsed.startDate) {
    const today = startOfDay(new Date());
    const daysBack = Math.floor((today - startOfDay(parsed.startDate)) / (1000 * 60 * 60 * 24));
    if (daysBack > RETROACTIVE_WARN_DAYS) {
      const msg = `Startdatum är ${daysBack} dagar bakåt i tiden — bekräfta att tilldelningen är retroaktiv`;
      warnings.push(msg);
      fieldWarnings.startDate = msg;
    }
  }
  if (parsed.endDate) {
    const today = startOfDay(new Date());
    if (startOfDay(parsed.endDate) < today) {
      const msg = "Slutdatum är passerat — tillståndet är redan avslutat (är detta historik-uppdatering?)";
      warnings.push(msg);
      fieldWarnings.endDate = msg;
    }
  }

  return { errors, warnings, fieldErrors, fieldWarnings, parsed };
};

/**
 * Cross-row-validering för övergångar (Kalle slutar → Lisa börjar på samma plats).
 *
 * @param {Array} rows - rader med { spotId, startDate (Date), endDate (Date|null), phone, ... }
 * @returns {Array<{ errors: string[], warnings: string[], fieldErrors: object, fieldWarnings: object }>}
 *          fieldErrors/fieldWarnings flaggar startDate-cellen som problemkällan (där "denna börjar").
 */
export const validateTransitions = (rows) => {
  const issues = rows.map(() => ({ errors: [], warnings: [], fieldErrors: {}, fieldWarnings: {} }));

  // Gruppera rader per plats
  const bySpot = new Map();
  rows.forEach((row, idx) => {
    if (!row.spotId || !row.parsedStartDate) return;
    if (!bySpot.has(row.spotId)) bySpot.set(row.spotId, []);
    bySpot.get(row.spotId).push({ ...row, idx });
  });

  // För varje plats, sortera efter startdatum och kolla overlap/gap
  bySpot.forEach((spotRows) => {
    if (spotRows.length < 2) return;
    spotRows.sort((a, b) => a.parsedStartDate - b.parsedStartDate);

    for (let i = 1; i < spotRows.length; i++) {
      const prev = spotRows[i - 1];
      const curr = spotRows[i];

      if (!prev.parsedEndDate) {
        const msg = `Plats är redan upptagen av rad ${prev.idx + 1} (ingen slutdatum) — kan inte starta nytt tillstånd här`;
        issues[curr.idx].errors.push(msg);
        issues[curr.idx].fieldErrors.startDate = msg;
        continue;
      }

      if (prev.parsedEndDate >= curr.parsedStartDate) {
        const msg = `Övergångskonflikt: rad ${prev.idx + 1} slutar ${prev.parsedEndDate.toISOString().slice(0, 10)} men denna börjar ${curr.parsedStartDate.toISOString().slice(0, 10)}`;
        issues[curr.idx].errors.push(msg);
        issues[curr.idx].fieldErrors.startDate = msg;
      } else {
        const gapDays = Math.floor((curr.parsedStartDate - prev.parsedEndDate) / (1000 * 60 * 60 * 24));
        if (gapDays > TRANSITION_GAP_WARN_DAYS) {
          const msg = `${gapDays} dagars gap till föregående tillstånd (rad ${prev.idx + 1}) — bekräfta att gapet är meningen`;
          issues[curr.idx].warnings.push(msg);
          issues[curr.idx].fieldWarnings.startDate = msg;
        }
      }
    }
  });

  return issues;
};

/**
 * Validera telefonnummer-format (basic — backend gör fullständig normalisering).
 * @returns {{ error?: string, normalized?: string }}
 */
export const validatePhone = (value) => {
  if (!value) return {};
  const trimmed = String(value).replace(/[\s\-()]/g, "");
  // Grov-koll först: bara siffror, ev. ledande + eller 0
  if (!/^(\+?\d{6,15}|0\d{6,10})$/.test(trimmed)) {
    return { error: `Telefonnummer har ogiltigt format: "${value}"` };
  }
  // Konvertera 0xx → +46xx (svensk default — matchar backend AssignmentService.NormalizePhone)
  const normalized = trimmed.startsWith("0") ? "+46" + trimmed.slice(1) : trimmed.startsWith("+") ? trimmed : "+" + trimmed;

  // Preventiv koll med libphonenumber: fånga ogiltiga nummer (fel längd osv) OCH flagga fast
  // telefoni redan vid upload — fastnät kan inte ta emot SMS. Se prod-incident 2026-06-15 där
  // ett Stockholms-fastnät (+46856642058) loopade 1900+ ggr i SMS-jobbet. Felformat blockerar
  // (error), trolig landline varnar men släpps igenom (warning) — undviker falsklarm på tvetydiga.
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const parsed = phoneUtil.parse(normalized, "SE");
    if (!phoneUtil.isValidNumber(parsed)) {
      return { error: `Telefonnummer är ogiltigt: "${value}"` };
    }
    if (phoneUtil.getNumberType(parsed) === PhoneNumberType.FIXED_LINE) {
      return { warning: `Numret "${value}" ser ut att vara ett fast telefonnummer — SMS kan inte skickas dit`, normalized };
    }
  } catch (e) {
    return { error: `Telefonnummer kunde inte tolkas: "${value}"` };
  }

  return { normalized };
};

/**
 * Formatera "Befintligt tillstånd"-kolumnens cellvärde för Excel-mallen.
 *
 * Matchar AssignmentService.cs överlappskontroll: marknadsplats-permits utan köpare
 * OCH framtidsschemalagda permits blockerar nya tilldelningar, så båda måste synas
 * här annars ljuger mallen för användaren (se incident 2026-05-15 — A10 markerad
 * "Inget" trots osåld marknadsplats-permit 1738 som blockerade upload).
 *
 * @param {object} spot - { hasActivePermit, activePermitStartDateTime, activePermitEndDateTime, activePermitIsMarketplace }
 * @returns {string}
 */
export const formatExistingPermitLabel = (spot) => {
  if (!spot || !spot.hasActivePermit) return "Inget";
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const start = spot.activePermitStartDateTime ? new Date(spot.activePermitStartDateTime) : null;
  const isFuture = start && start > today;
  const endLabel = spot.activePermitEndDateTime
    ? new Date(spot.activePermitEndDateTime).toISOString().slice(0, 10)
    : "tillsvidare";

  if (spot.activePermitIsMarketplace) {
    return `Marknadsplats — ${endLabel}`;
  }
  if (isFuture) {
    return `Schemalagd från ${start.toISOString().slice(0, 10)} — ${endLabel}`;
  }
  return spot.activePermitEndDateTime
    ? new Date(spot.activePermitEndDateTime).toISOString().slice(0, 10)
    : "Tillsvidare";
};

/**
 * Validera regnummer-format (svenska + breda — backend lagrar bara strängen som-är).
 * @returns {{ error?: string, normalized?: string }}
 */
export const validateRegistrationPlate = (value) => {
  if (!value) return {};
  const trimmed = String(value).trim().toUpperCase().replace(/\s/g, "");
  if (trimmed.length < 2 || trimmed.length > 10) {
    return { error: `Regnummer har orimlig längd: "${value}"` };
  }
  if (!/^[A-Z0-9-]+$/.test(trimmed)) {
    return { error: `Regnummer innehåller ogiltiga tecken: "${value}"` };
  }
  return { normalized: trimmed };
};
